import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { usePaginationShortcuts } from "@/hooks/use-pagination-shortcuts";
import IconChevronLeft from "central-icons/IconChevronLeft";
import IconChevronRight from "central-icons/IconChevronRight";
import { useCallback, type FC } from "react";

export type CursorPaginationProps = {
    /** Navigate to the next page */
    onNext: () => void;
    /** Navigate to the previous page */
    onPrevious: () => void;
    /** Whether a next page exists */
    hasNextPage: boolean;
    /** Whether a previous page exists */
    hasPreviousPage: boolean;
    /** Loading state */
    isLoading?: boolean;
    /** Additional CSS classes */
    className?: string;
    /** Number of items on the current page */
    currentItemsCount?: number;
};

/**
 * Cursor-based pagination with only prev/next buttons.
 * Use when the API uses opaque cursor tokens without total counts.
 */
export const CursorPagination: FC<CursorPaginationProps> = ({
    onNext,
    onPrevious,
    hasNextPage,
    hasPreviousPage,
    isLoading = false,
    className,
    currentItemsCount = 0,
}) => {
    const handlePrevious = useCallback(() => {
        if (hasPreviousPage) {
            onPrevious();
        }
    }, [hasPreviousPage, onPrevious]);

    const handleNext = useCallback(() => {
        if (hasNextPage) {
            onNext();
        }
    }, [hasNextPage, onNext]);

    usePaginationShortcuts({
        onPrevious: handlePrevious,
        onNext: handleNext,
        enabled: !isLoading,
    });

    if (!hasNextPage && !hasPreviousPage) {
        return null;
    }

    const itemText = currentItemsCount > 0 ? `Showing ${currentItemsCount} results` : "";

    return (
        <div className="w-full @container/nav">
            <div
                className={cn(
                    "flex w-full flex-col items-center justify-between gap-4 py-8 @sm/nav:flex-row @sm/nav:px-6",
                    className,
                )}
            >
                <div className="flex-shrink text-sm text-content-secondary">{itemText}</div>

                <nav role="navigation" aria-label="pagination" className="flex items-center gap-1">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handlePrevious}
                        disabled={!hasPreviousPage}
                        aria-label="Go to previous page"
                        data-tracking-id="cursor-pagination-previous"
                        className="aspect-square p-0"
                    >
                        <IconChevronLeft className="size-3.5" />
                        <span className="sr-only">Previous</span>
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleNext}
                        disabled={!hasNextPage}
                        aria-label="Go to next page"
                        data-tracking-id="cursor-pagination-next"
                        className="aspect-square p-0"
                    >
                        <span className="sr-only">Next</span>
                        <IconChevronRight className="size-3.5" />
                    </Button>
                </nav>
            </div>
        </div>
    );
};
